import { programFromConfig, ladderState } from "../components/SalesBonus/moneyRules";

/**
 * The team average, as a rep.
 *
 * Anticipated Earnings and Milestones & Bonuses are both built around a list of
 * `reps` and ONE selection: an admin lands on `reps[0]` and can switch player
 * from the card's own picker. So the average does not need a second card or a
 * parallel code path — it needs to BE a rep, sitting first in the list.
 *
 * An admin therefore opens the board on "the average of the entire team" (the
 * head-of-sales view) and can still drop into any individual from the same
 * picker, which is exactly how the card already behaves.
 *
 * The synthetic row is tagged with a reserved ownerId so the roster views ("All
 * players") can leave it out — an average is not a person to rank.
 */

export const AVG_ID = "__team_avg__";

export const isAverageRep = (r) => !!r && r.ownerId === AVG_ID;

/** Reps that are real people — the roster, without the synthetic average. */
export const realReps = (reps) => (reps || []).filter((r) => !isAverageRep(r));

/**
 * Live money-card roster only. Same rule as KOTM `isLiveBoardPlayer`:
 * missing from the company dictionary = DROP (archived users are often absent
 * from GET_COMPANY_USERS — keeping unknowns was how Wesley Johnson stayed on
 * Anticipated earnings / milestone pickers forever).
 */
export const isLiveMoneyRep = (usersDict, rep) => {
  if (!rep || isAverageRep(rep)) return false;
  const id = rep.ownerId != null ? String(rep.ownerId) : "";
  if (!id) return false;
  if (rep.isArchived === true || rep.isActive === false) return false;
  const u = usersDict?.[id] || usersDict?.[rep.ownerId];
  if (!u || typeof u !== "object") return false;
  if (u.isArchived === true || u.isActive === false) return false;
  if (u.lastName === "Yeti" || u.isYeti || u.isAdmin) return false;
  return true;
};

/**
 * The average is PER SCORING PLAYER, not per head — the same rule UseMyKpis and
 * the metric tiles already use. A rep who has not scored yet (a zero on the
 * measured field) is not in the divisor; counting them dragged a team of a few
 * earners toward zero and made "Team average" read 0 across the board. When no
 * one has scored a field, its average is genuinely 0.
 */
const mean = (rows, pick) => {
  const scored = rows.filter((r) => (Number(pick(r)) || 0) > 0);
  if (!scored.length) return 0;
  return scored.reduce((sum, r) => sum + (Number(pick(r)) || 0), 0) / scored.length;
};

const total = (rows, pick) => rows.reduce((sum, r) => sum + (Number(pick(r)) || 0), 0);

/* Union of every rep's `amounts` keys (per-metric buckets). */
const amountKeys = (rows) => {
  const keys = new Set();
  rows.forEach((r) => Object.keys(r?.amounts || {}).forEach((k) => keys.add(k)));
  return [...keys];
};

/* Each `amounts[kpiId]` is an OBJECT ({ month, year, … }), not a scalar — the
   cards read amounts[payKpiId].month. Roll up each numeric sub-field and keep
   the object shape, so the average rep looks like a real rep. (The old code
   averaged the object itself → NaN → 0, which is why the headline earnings on
   the team-average row read $0.) Non-numeric sub-fields, e.g. `plans`, are left
   off the rolled-up row — it has no personal plan history. */
const rollAmounts = (rows, roll) => {
  const out = {};
  amountKeys(rows).forEach((k) => {
    const fields = new Set();
    rows.forEach((r) => {
      const a = r?.amounts?.[k];
      if (a && typeof a === "object") {
        Object.keys(a).forEach((f) => { if (typeof a[f] === "number") fields.add(f); });
      }
    });
    if (fields.size) {
      const obj = {};
      fields.forEach((f) => { obj[f] = roll(rows, (r) => r?.amounts?.[k]?.[f]); });
      out[k] = obj;
    } else {
      // Legacy scalar amount (older payloads).
      out[k] = roll(rows, (r) => (r.amounts || {})[k]);
    }
  });
  return out;
};

/* A rep counts as "scored" if they have any activity at all — used only for the
   average row's player count label. */
const hasActivity = (r) => {
  if (!r) return false;
  if ((Number(r.month) || 0) > 0 || (Number(r.ytd) || 0) > 0 || (Number(r.lastMonth) || 0) > 0
    || (Number(r.commission) || 0) > 0 || (Number(r.earned) || 0) > 0) return true;
  const a = r.amounts || {};
  return Object.keys(a).some((k) => {
    const v = a[k];
    if (v && typeof v === "object") return Object.keys(v).some((f) => typeof v[f] === "number" && v[f] > 0);
    return (Number(v) || 0) > 0;
  });
};

/**
 * Add the team-average rep to a bonus payload, first in the list.
 *
 * Returns the payload untouched when there is nothing to average (no reps, or a
 * single rep — the "average" of one person is that person, and offering it
 * would just be the same numbers under a different name).
 *
 * The editor's Show control picks which one leads: "avg" (the default) folds in
 * a Team average row; "total" folds in a Team total row that sums everyone. It
 * is always the FIRST rep, so an admin opens on the team figure and can still
 * pick any individual from the card's own dropdown.
 *
 * @param {object} data  the bonus payload: { reps, totals, config, myId, … }
 * @param {string} mode  "avg" (default) or "total"
 */
export const withTeamAverage = (data, mode = "avg") => {
  const reps = realReps(data && data.reps);
  if (reps.length < 2) return data;

  const isTotal = mode === "total";
  const roll = isTotal ? total : mean;

  const ytd = roll(reps, (r) => r.ytd);
  /* Milestone money follows the rolled-up YTD on the ladder — NOT the average
     of each player's `earned`. Averaging earned let one rep who cleared rungs
     in August show $32,500 "earned" while the team-average meter was still
     $8,464 short of the first $250K rung. */
  const program = programFromConfig(data && data.config);
  const earned = ladderState(program, ytd).banked;

  /* Combined pay Applies-to windows: each range has its own amounts bag on
     the rep. Roll those the same way as `amounts` so Team average / total
     lines use Q1–Q2 vs Q3 slices correctly. */
  const appliesKeys = new Set();
  reps.forEach((r) => Object.keys(r?.appliesAmounts || {}).forEach((k) => appliesKeys.add(k)));
  const appliesAmounts = {};
  appliesKeys.forEach((rangeKey) => {
    const pseudo = reps.map((r) => ({ amounts: (r.appliesAmounts && r.appliesAmounts[rangeKey]) || {} }));
    appliesAmounts[rangeKey] = rollAmounts(pseudo, roll);
  });

  const row = {
    ownerId: AVG_ID,
    name: isTotal ? "Team total" : "Team average",
    image: "",
    month: roll(reps, (r) => r.month),
    lastMonth: roll(reps, (r) => r.lastMonth),
    ytd,
    commission: roll(reps, (r) => r.commission),
    earned,
    amounts: rollAmounts(reps, roll),
    ...(Object.keys(appliesAmounts).length ? { appliesAmounts } : {}),
    /* Cleared months belong to a person's own run up the ladder; a rolled-up
       row has not personally cleared anything, so the card derives its rungs
       from ytd. */
    clearedMonths: {},
    /* An average is "across the players who have scored", so its own count is
       the scorers, not the full roster (a total spans everyone). */
    players: isTotal ? reps.length : (reps.filter(hasActivity).length || reps.length),
  };

  return { ...data, reps: [row, ...reps] };
};

export default withTeamAverage;
